import React, { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import ProtectedRoute from "./Components/ProtectedRoute";
import BugzillaPrivateRoute from "./Components/BugzillaProtectRoute";
import {
  adminsLoader,
  bugzillaBugsLoader,
  bugzillaDashboardLoader,
  bugzillAddUserLoader,
  bugzillBugCreateLoader,
  bugzillBugViewLoader,
  bugzillUserLoader,
  dashboardLoader,
  paymentsLoader,
  pricingLoader,
  projectsLoader,
  renewalsLoader,
  testimonialsLoader,
  userLoader,
} from "./Loaders/allPagesLoaders";
import { bugzillaLoginLoader } from "./Loaders/apiLoader";
import RouteErrorModal from "./Loaders/ErrorStack";

/* ================= Lazy Components ================= */
const NotFound = lazy(() => import("./Pages/NotFound"));
const LoginPage = lazy(() => import("./Pages/AdminLogin"));
const Dashboard = lazy(() => import("./Pages/Dashboard"));
const Navbar = lazy(() => import("./Components/Navbar"));
const Projects = lazy(() => import("./Pages/Projects"));
const ProjectsRenewals = lazy(() => import("./Pages/RenewalsPage"));
const AddProjectPage = lazy(() => import("./Pages/AddProject"));
const Payments = lazy(() => import("./Pages/Payments"));
const BrandData = lazy(() => import("./Pages/BrandData"));
const CreateAdmin = lazy(() => import("./Pages/CreateAdmin"));
const ProfileDetails = lazy(() => import("./Pages/ProfileDetails"));
const Bugzill = lazy(() => import("./Pages/BugzillLogin"));
const BugzillaDashboard = lazy(() => import("./Pages/BugzillaDashboard"));
const BugzillaLayout = lazy(() => import("./Pages/BugzillaLayout"));
const AllBugs = lazy(() => import("./Pages/BugzillAllBugs"));
const BugzillNewBugCreate = lazy(() => import("./Pages/BugzillNewBugCreate"));
const BugView = lazy(() => import("./Pages/BugzillBugView"));
const MyBugs = lazy(() => import("./Pages/BugzillaMyBugIds"));
const AddBugzillaUser = lazy(() => import("./Pages/BugzillaAddUser"));
const UserProfile = lazy(() => import("./Pages/BugzillaUserProfile"));
const ManageBlogs = lazy(() => import("./Pages/ManageBlogs"));
const TestimonialsManage = lazy(() => import("./Components/TestimonialManage"));

/* ================= Loader-safe Suspense ================= */
const withSuspense = (Component) => (
  <Suspense
    fallback={
      <div className="full-page-loader">
        <div className="loader-spinner"></div>
        <p>Loading, please wait...</p>
      </div>
    }
  >
    <Component />
  </Suspense>
);

/* ================= Router ================= */
export const router = createBrowserRouter([
  {
    path: "/",
    element: withSuspense(LoginPage),
  },

  {
    path: "/admin",
    element: <ProtectedRoute>{withSuspense(Navbar)}</ProtectedRoute>,
    loader: userLoader,
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: "dashboard",
        element: withSuspense(Dashboard),
        loader: dashboardLoader,
      },
      {
        path: "projects",
        element: withSuspense(Projects),
        // errorElement: LoaderErrorModal,
        loader: projectsLoader,
      },
      {
        path: "add-project",
        element: withSuspense(AddProjectPage),
        loader: pricingLoader,
      },
      {
        path: "payments",
        element: withSuspense(Payments),
        loader: paymentsLoader,
      },
      {
        path: "brand-data",
        element: withSuspense(BrandData),
        loader: pricingLoader,
      },

      {
        path: "testimonials-manage",
        element: withSuspense(TestimonialsManage),
        loader: testimonialsLoader,
      },
      {
        path: "blogs-manage",
        element: withSuspense(ManageBlogs),
        loader: pricingLoader,
      },
      {
        path: "create-admin",
        element: withSuspense(CreateAdmin),
        loader: adminsLoader,
      },
      {
        path: "profile",
        element: withSuspense(ProfileDetails),
        loader: userLoader,
      },
      {
        path: "renewals",
        element: withSuspense(ProjectsRenewals),
        loader: renewalsLoader,
      },

      /* ========== Bugzilla ========== */
      {
        path: "bugzilla/login",
        element: withSuspense(Bugzill),
        loader: bugzillaLoginLoader,
      },
      {
        path: "bugzilla",
        element: (
          <BugzillaPrivateRoute>
            {withSuspense(BugzillaLayout)}
          </BugzillaPrivateRoute>
        ),
        loader: bugzillUserLoader,
        errorElement: RouteErrorModal,
        children: [
          {
            index: true,
            element: withSuspense(BugzillaDashboard),
            errorElement: RouteErrorModal,
          },
          {
            path: "user/:id/dashboard",
            element: withSuspense(BugzillaDashboard),
            loader: bugzillaDashboardLoader,
            errorElement: RouteErrorModal,
          },
          {
            path: "user/:userid/all-bugs",
            element: withSuspense(AllBugs),
            loader: bugzillaBugsLoader,
          },
          {
            path: "user/:userid/my-bugs",
            element: withSuspense(MyBugs),
            loader: bugzillaBugsLoader,
          },
          {
            path: "user/:userid/add-user",
            element: withSuspense(AddBugzillaUser),
            loader: bugzillAddUserLoader,
          },
          {
            path: "user/:userid/profile",
            element: withSuspense(UserProfile),
            loader: bugzillUserLoader,
          },
          {
            path: "user/:id/create-bug",
            element: withSuspense(BugzillNewBugCreate),
            loader: bugzillBugCreateLoader,
          },
          {
            path: "user/:userId/all-bugs/:bugid",
            element: withSuspense(BugView),
            loader: bugzillBugViewLoader,
          },
        ],
      },
    ],
  },

  {
    path: "*",
    element: withSuspense(NotFound),
  },
]);
